import { useState, useEffect, useCallback } from 'react';

export interface ComparisonStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  completedModels: number;
  totalModels: number;
  title?: string;
  prompt?: string;
  modelsUsed?: string[];
  error?: string;
}

interface UseComparisonStatusOptions {
  comparisonId: string | undefined;
  pollInterval?: number; // milliseconds, default 3000
  enabled?: boolean; // Allow pausing polling
}

/**
 * Hook to poll comparison status from the API
 *
 * Milestone 3: Uses dedicated /api/comparison/progress?id=xxx endpoint for real-time updates
 * Feature Flag: COMPARISON_FLOW_POC (when enabled, polls real backend)
 */
export function useComparisonStatus({
  comparisonId,
  pollInterval = 3000,
  enabled = true
}: UseComparisonStatusOptions) {
  const [status, setStatus] = useState<ComparisonStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!comparisonId || !enabled) return;

    try {
      // Use dedicated progress endpoint with path parameter (Milestone 3)
      // Note: comparisonId can be either UUID or slug (backend handles both)
      const response = await fetch(`/api/comparison/${comparisonId}/progress`);
      if (!response.ok) {
        throw new Error(`Failed to fetch comparison progress: ${response.status}`);
      }

      const data = await response.json();

      // Extract progress object from response
      const progress = data.progress || data;

      setStatus({
        id: progress.id || comparisonId, // Use UUID from API (fallback to param for safety)
        status: progress.status || 'pending',
        completedModels: progress.completedModels || 0,
        totalModels: progress.totalModels || 0,
        title: progress.title,
        prompt: progress.prompt,
        modelsUsed: progress.modelsUsed,
        error: progress.error || undefined
      });
      setError(null);
      setIsLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch status';
      console.error('[useComparisonStatus] Error:', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [comparisonId, enabled]);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling interval - stops automatically when completed or failed
  useEffect(() => {
    if (!enabled || !comparisonId) return;

    // Stop polling if comparison is completed or failed
    if (status && (status.status === 'completed' || status.status === 'failed')) {
      console.log(`[useComparisonStatus] Polling stopped - status: ${status.status}`);
      return;
    }

    const interval = setInterval(fetchStatus, pollInterval);
    return () => clearInterval(interval);
  }, [fetchStatus, pollInterval, enabled, comparisonId, status]);

  return {
    status,
    isLoading,
    error,
    refetch: fetchStatus
  };
}
